<script>
import { mapGetters, mapActions } from "vuex";
import axios from "axios";
import examApi from "../service/exam-api";

import genders from "../service/genders";
import { bitrixSourceLabel } from "../service/bitrix-sources";
import AdminAppShowDetail from "./AdminAppShowDetail.vue";
import AdminAppShowPhoto from "./AdminAppShowPhoto.vue";
import AppConfirm from "./modals/app-confirm.vue";
import UploadFileAdmin from "./UploadFileAdmin.vue";
import CopiableText from "@/components/CopiableText.vue";
import DatePicker from "./DatePicker.vue";
import app from "~/src/App.vue";

export default {
  components: { AdminAppShowDetail, AdminAppShowPhoto, AppConfirm, UploadFileAdmin, CopiableText, DatePicker },
  data: () => ({
    genders,

    photo: null,

    isEditing: false,
    isSaving: false,
    editForm: {},
    // Set when the admin changes Hujjat turi mid-edit; forces Ta'lim yo'nalishi re-selection.
    typeAbiturientChanged: false,
    // Live "this phone is already on another application" check — see queuePhoneDuplicateCheck.
    phoneDuplicate: false,
    phoneDuplicateTimer: null,
    examDropOpen: false,

    // Exam result — examAttempts holds every attempt (oldest first); examResult mirrors the
    // currently-selected one so the whole exam card renders unchanged. selectedAttemptIndex
    // defaults to the latest attempt.
    examResult: null,
    examAttempts: [],
    selectedAttemptIndex: 0,
    examResultLoading: false,

    // Bitrix deal (live crm.deal.get) — full raw fields
    bitrixDeal: null,
    bitrixLoading: false,
    bitrixError: false,
    bitrixCollapsed: true, // section starts collapsed; resets every load (no persistence)

    // Dropdown open states
    facultyDropOpen: false,
    typeDropOpen: false,
    languageDropOpen: false,
    typeAbitDropOpen: false,
    countryDropOpen: false,
    regionDropOpen: false,
    cityDropOpen: false,

    // Hujjat turi (document type) options for the custom dropdown.
    typeAbitOptions: [
      { value: "ABITURIENT", label: "Abituriyent" },
      { value: "MASTER", label: "Magistratura" },
      { value: "PEREVOD", label: "O'qishni ko'chirish" },
      { value: "SECOND_SPES", label: "Ikkinchi mutaxassislik" },
    ],

    // Search queries for filtering
    facultySearch: "",
    countrySearch: "",
    regionSearch: "",
    citySearch: "",

    // Loading states for cascading
    loadingTypes: false,
    loadingLanguages: false,
    loadingRegions: false,
    loadingCities: false,

    // Exam-config visibility for the selected faculty (from /exam-questions
    // "Imtihon sozlamalari"). Used to restrict the Ta'lim turi dropdown for
    // ABITURIENT applications to the education types ticked there.
    examTypeVisibility: null,

    acceptDialog: false,
    passedDialog: false,
    rejectDialog: false,
    paidDialog: false,

    // Matrix-driven permissions (from /admin/panel-permissions/check)
    canUpdate: false,
    canDelete: false,
    // QABUL panel — gates the Accept ("Qabul qilish") / Reject ("Rad etish") buttons
    canQabul: false,

    // Faculty IDs that have a 1-kurs price for the current admissions year.
    // Second line of defense for the Ta'lim yo'nalishi dropdown: the list is
    // requested with pricedOnly=true, but an older API ignores that param and
    // returns everything — this Set still narrows it client-side then.
    pricedFacultyIds: new Set(),
  }),

  computed: {
    ...mapGetters("admin-applications", ["GET_APPLICATION"]),
    application: (vm) => vm.GET_APPLICATION,

    /** Created by the DTM xlsx bulk import — application.source, sent by the API. */
    isExcelImported: (vm) => vm.application && vm.application.source === "DTM_IMPORT",

    /* Per-field format errors. Empty fields are NOT errors (admins can save
     * partial data) — but a field with a half-typed value that doesn't match
     * its mask format gets a red border so the admin can tell at a glance. */
    fieldErrors() {
      const f = this.editForm || {};
      const e = {};
      const reLatin    = /^[a-zA-Z'` ]+$/;
      const rePassport = /^[A-Z]{2}\d{7}$/;
      const reJshir    = /^\d{14}$/;
      // Phones hold only the 9 subscriber digits here; the "+998" prefix lives
      // in the mask literal and is re-attached on save (see toFullPhone).
      const rePhone    = /^\d{9}$/;
      if (f.firstName  && !reLatin.test(f.firstName))     e.firstName = true;
      if (f.lastName   && !reLatin.test(f.lastName))      e.lastName  = true;
      if (f.middleName && !reLatin.test(f.middleName))    e.middleName = true;
      if (f.passportNumber && !rePassport.test(f.passportNumber)) e.passportNumber = true;
      if (f.jshir       && !reJshir.test(f.jshir))        e.jshir = true;
      if (f.mobilePhone && !rePhone.test(f.mobilePhone))  e.mobilePhone = true;
      // A phone another application already holds blocks the save rather than just warning:
      // the backend's @ValidUniqueMobilePhone guard would reject it anyway.
      if (this.phoneDuplicate) e.mobilePhone = true;
      if (f.additionalPhone && !rePhone.test(f.additionalPhone)) e.additionalPhone = true;
      if (f.homePhone   && !rePhone.test(f.homePhone))    e.homePhone   = true;
      if (f.fatherPhone && !rePhone.test(f.fatherPhone))  e.fatherPhone = true;
      if (f.motherPhone && !rePhone.test(f.motherPhone))  e.motherPhone = true;
      // After Hujjat turi changes, Ta'lim yo'nalishi is wiped and must be re-picked
      // before saving (Magistratura and Abituriyent have different direction lists).
      if (this.typeAbiturientChanged) {
        if (!f.educationFacultyId)  e.educationFaculty  = true;
        if (!f.educationTypeId)     e.educationType     = true;
        if (!f.educationLanguageId) e.educationLanguage = true;
      }
      return e;
    },
    hasFormErrors() {
      return Object.keys(this.fieldErrors).length > 0;
    },

    ...mapGetters("auth", ["GET_USER"]),
    user: (vm) => vm.GET_USER,

    ...mapGetters("admin-application", [
      "GET_EDUCATION_FACULTYS",
      "GET_EDUCATION_TYPES",
      "GET_EDUCATION_LANGUAGES",
      "GET_COUNTRYS",
      "GET_REGIONS",
      "GET_CITYS",
    ]),

    countries() {
      return this.GET_COUNTRYS || [];
    },
    regions() {
      return this.GET_REGIONS || [];
    },
    cities() {
      return this.GET_CITYS || [];
    },

    educationFaculties() {
      return this.GET_EDUCATION_FACULTYS || [];
    },
    educationTypes() {
      return this.GET_EDUCATION_TYPES || [];
    },
    educationLanguages() {
      const seen = new Set();
      return (this.GET_EDUCATION_LANGUAGES || [])
        .map((l) => ({
          id: l.language?.id,
          name: l.language?.name,
          scienceComplex: l.scienceComplex,
        }))
        .filter((l) => {
          if (seen.has(l.id)) return false;
          seen.add(l.id);
          return true;
        });
    },

    // True only for the entrance-exam flow. PEREVOD / SECOND_SPES / MASTER / DTM
    // applicants don't sit the exam, so the exam-config visibility never applies.
    isAbiturientApp() {
      return !!(this.application && this.application.typeAbiturient === "ABITURIENT");
    },
    // Parsed allowedEducationTypeIds ("1,4") -> Set{1,4}. null means "no restriction
    // configured" (faculty has no exam config) -> show every offered type.
    allowedExamTypeSet() {
      const raw = this.examTypeVisibility && this.examTypeVisibility.allowedEducationTypeIds;
      if (!raw) return null;
      const set = new Set(String(raw).split(",").map(Number).filter(Boolean));
      return set.size ? set : null;
    },

    // Option lists augmented with the currently-selected saved value, so a pairing
    // that was withdrawn from config (no education_faculty_type row) still shows and
    // stays selectable. Gated on the saved item still being the active selection, so
    // a stale value is not injected after the admin changes faculty/type.
    displayTypes() {
      let list = [...this.educationTypes];
      // For ABITURIENT applications the Ta'lim turi options must mirror what is
      // ticked in Imtihon sozlamalari (/exam-questions): keep only education types
      // present in exam-config allowedEducationTypeIds. Intersection with the
      // faculty's offered types means a type that's "allowed" but has no
      // education_faculty_type row (e.g. 3-smena on a faculty without it) won't
      // surface a dead option with no languages.
      if (this.isAbiturientApp && this.allowedExamTypeSet) {
        list = list.filter((t) => this.allowedExamTypeSet.has(Number(t.id)));
      }
      const sel = this.application && this.application.educationType;
      if (sel && sel.id && this.editForm.educationTypeId == sel.id
          && !list.some((t) => t.id == sel.id)) {
        list.unshift({ id: sel.id, name: sel.name });
      }
      return list;
    },
    displayLanguages() {
      const list = [...this.educationLanguages];
      const sel = this.application && this.application.educationLanguage;
      if (sel && sel.id && this.editForm.educationLanguageId == sel.id
          && !list.some((l) => l.id == sel.id)) {
        list.unshift({ id: sel.id, name: sel.name, scienceComplex: sel.scienceComplex });
      }
      return list;
    },

    filteredFaculties() {
      const priced = this.pricedFacultyIds;
      const havePricedList = priced && priced.size > 0;
      let list = this.educationFaculties;
      if (havePricedList) {
        list = list.filter((f) => priced.has(Number(f.id)));
      }
      if (!this.facultySearch) return list;
      const q = this.facultySearch.toLowerCase();
      return list.filter((f) => (f.name || "").toLowerCase().includes(q));
    },

    /* ── Mamlakat / Viloyat / Tuman ──
     * Same shape as the faculty dropdown: a search box over the loaded list, and a
     * selected-name fallback to the value saved on the application so the trigger
     * never reads "—" while the cascading list is still loading (or when a legacy
     * row points at an entry the list no longer returns). */
    filteredCountries() {
      return this.filterByName(this.countries, this.countrySearch);
    },
    filteredRegions() {
      return this.filterByName(this.regions, this.regionSearch);
    },
    filteredCities() {
      return this.filterByName(this.cities, this.citySearch);
    },

    selectedCountryName() {
      return this.pickedName(this.countries, this.editForm.countryId, this.application && this.application.country);
    },
    selectedRegionName() {
      return this.pickedName(this.regions, this.editForm.regionId, this.application && this.application.region);
    },
    selectedCityName() {
      return this.pickedName(this.cities, this.editForm.cityId, this.application && this.application.city);
    },

    selectedFacultyName() {
      const f = this.educationFaculties.find((x) => x.id == this.editForm.educationFacultyId);
      if (f) return f.name;
      // The list now carries only 1-kurs-priced directions; a legacy application may
      // still point at one that was filtered out — show its saved name, not "—".
      const sel = this.application && this.application.educationFaculty;
      if (sel && sel.id && this.editForm.educationFacultyId == sel.id && sel.name) {
        return sel.name;
      }
      return "—";
    },
    selectedTypeName() {
      const t = this.displayTypes.find((x) => x.id == this.editForm.educationTypeId);
      return t ? t.name : "—";
    },
    selectedLanguageName() {
      const l = this.displayLanguages.find((x) => x.id == this.editForm.educationLanguageId);
      return l ? l.name : "—";
    },
    selectedTypeAbitLabel() {
      const o = this.typeAbitOptions.find((x) => x.value === this.editForm.typeAbiturient);
      return o ? o.label : "Tanlang";
    },

    isStaj: (vm) =>
      vm.application.educationFaculty.id == 4 &&
      vm.application.educationType.id == 3,
    // DTM arrival. Mirrors the server-side predicate exactly:
    //   dtmScore IS NOT NULL AND dtmScore <> ''
    // NOT typeAbiturient == 'DTM' — DTM applicants intentionally keep
    // typeAbiturient = ABITURIENT so contract price and kurs stay correct.
    isDtm: (vm) => {
      const s = vm.application && vm.application.dtmScore;
      return s != null && String(s).trim() !== "";
    },
    isReadonly: (vm) => vm.user.role == "ADMIN_OBSERVER",
    isSuperAdmin: (vm) => vm.user.role == "ADMIN_SUPER",
    isQabulQiluvchi: (vm) => vm.user.role == "QABUL_QILUVCHI",
    isHasPinflData: (vm) => vm.user.hasPinflData,

    isDark() {
      return this.$vuetify?.theme?.dark || false;
    },

    examResultBySubject() {
      const list = this.examResult?.questions || [];
      if (!list.length) return [];
      const map = new Map();
      for (const q of list) {
        const name = (q && q.scienceName) ? q.scienceName : this.$t("Boshqa");
        let row = map.get(name);
        if (!row) {
          row = { name, total: 0, correct: 0 };
          map.set(name, row);
        }
        row.total += 1;
        if (q.isCorrect) row.correct += 1;
      }
      return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    },

    hasMultipleAttempts() {
      return this.examAttempts.length > 1;
    },
  },

  watch: {
    'application.id': {
      immediate: true,
      handler(id) {
        if (id) {
          this.bitrixCollapsed = true; // reset section to collapsed for each applicant
          this.fetchExamResult(id);
          this.fetchBitrixDeal(id);
          this.restoreEditDraft(id);
        }
      },
    },
    editForm: {
      deep: true,
      handler() {
        if (this.isEditing) this.persistEditDraft();
      },
    },
    "editForm.mobilePhone"(v) {
      this.queuePhoneDuplicateCheck(v);
    },
    isEditing(v) {
      if (v) this.persistEditDraft();
    },
  },

  async mounted() {
    this._onClickOutside = (e) => {
      if (!this.$el.contains(e.target)) {
        this.closeAllDropdowns();
      }
    };
    document.addEventListener("click", this._onClickOutside);

    this.canUpdate = await this.hasRole("ARIZALAR", "UPDATE");
    this.canDelete = await this.hasRole("ARIZALAR", "DELETE");
    this.canQabul = await this.hasRole("QABUL", "READ");

    try {
      const year = new Date().getFullYear();
      const studyYear = `${year}-${year + 1}`;
      const ids = await this.$axios.$get(
        `admin/faculty/ids-with-price?kurs=1&studyYear=${studyYear}`
      );
      this.pricedFacultyIds = new Set((ids || []).map(Number));
    } catch (e) {
      console.warn("Failed to load priced faculty ids; dropdown will show all faculties:", e);
    }
  },

  beforeDestroy() {
    document.removeEventListener("click", this._onClickOutside);
  },

  methods: {
    sourceLabel(v) {
      return bitrixSourceLabel(v);
    },
    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },

    app() {
      return app;
    },

    /* Input restrictions are now enforced declaratively via v-inputmask
     * with the project's shared $masks (text / passport / numberLength12 /
     * phone) — matches what AdminContractPaidEdit2026 and friends use. */

    async fetchExamResult(applicationId) {
      this.examResultLoading = true;
      this.examAttempts = [];
      this.examResult = null;
      this.selectedAttemptIndex = 0;
      try {
        // Every attempt, oldest first. A failed retake keeps its finished session, so a student
        // can have several — show them all, defaulting to the latest.
        const { data } = await examApi.get(`admin/exam/results/by-application/${applicationId}/attempts`);
        const attempts = Array.isArray(data) ? data : [];
        if (attempts.length) {
          this.examAttempts = attempts;
          this.selectedAttemptIndex = attempts.length - 1;
          this.examResult = attempts[this.selectedAttemptIndex];
          return;
        }
        // No attempts from the new endpoint: fall back to the single-latest endpoint so nothing
        // regresses while the backend that serves /attempts is still rolling out.
        const { data: single, status } = await examApi.get(`admin/exam/results/by-application/${applicationId}`);
        this.examResult = status === 204 ? null : (single || null);
        this.examAttempts = this.examResult ? [this.examResult] : [];
      } catch {
        this.examResult = null;
        this.examAttempts = [];
      } finally {
        this.examResultLoading = false;
      }
    },

    selectAttempt(index) {
      if (index < 0 || index >= this.examAttempts.length) return;
      this.selectedAttemptIndex = index;
      this.examResult = this.examAttempts[index];
    },

    async fetchBitrixDeal(applicationId) {
      this.bitrixLoading = true;
      this.bitrixError = false;
      try {
        const { data } = await examApi.get(`admin/applications/${applicationId}/bitrix-deal`);
        this.bitrixDeal = data;
      } catch {
        this.bitrixDeal = null;
        this.bitrixError = true;
      } finally {
        this.bitrixLoading = false;
      }
    },

    formatBitrixValue(v) {
      if (v === null || v === undefined || v === "") return "—";
      if (typeof v === "object") return JSON.stringify(v);
      return String(v);
    },

    editDraftKey(id) {
      return `app-show.edit-draft.${id}`;
    },

    persistEditDraft() {
      const id = this.application?.id;
      if (!id) return;
      try {
        localStorage.setItem(this.editDraftKey(id), JSON.stringify(this.editForm));
      } catch (_) { /* quota / private mode — silent */ }
    },

    clearEditDraft(id) {
      try {
        localStorage.removeItem(this.editDraftKey(id ?? this.application?.id));
      } catch (_) { /* ignore */ }
    },

    async restoreEditDraft(id) {
      let raw;
      try {
        raw = localStorage.getItem(this.editDraftKey(id));
      } catch (_) { return; }
      if (!raw) return;
      let saved;
      try { saved = JSON.parse(raw); } catch (_) { return; }
      if (!saved || typeof saved !== "object") return;

      // Build a fresh editForm from the current application, then layer the
      // saved draft on top so renamed/added fields don't blow away.
      await this.startEdit();
      const merged = { ...this.editForm, ...saved };
      // Older drafts may store phones in full (12-digit) form; re-normalize to
      // the 9 subscriber digits the +998 mask edits.
      ["mobilePhone", "additionalPhone", "homePhone", "fatherPhone", "motherPhone"].forEach((k) => {
        merged[k] = this.toSubscriber(merged[k]);
      });
      this.editForm = merged;

      // startEdit() loaded the region/city lists for the SAVED country/region; if the
      // draft moved either one, re-fetch so the restored Viloyat / Tuman resolve to a
      // name instead of falling back to the (now unrelated) saved application value.
      const a = this.application || {};
      if (merged.countryId != (a.country?.id || null) || merged.regionId != (a.region?.id || null)) {
        await this.loadAddressLists(merged.countryId, merged.regionId);
      }
    },

    formatExamDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      return d.toLocaleDateString("uz-UZ", { year: "numeric", month: "2-digit", day: "2-digit" })
        + " " + d.toLocaleTimeString("uz-UZ", { hour: "2-digit", minute: "2-digit" });
    },

    formatDuration(seconds) {
      if (!seconds) return "—";
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m} min ${s > 0 ? s + ' sec' : ''}`.trim();
    },
    ...mapActions("admin-applications", [
      "ACCEPT_APPLICATION",
      "PASSED_APPLICATION",
      "REJECT_APPLICATION",
      "PAID_APPLICATION",
      "SUGGESTED_APPLICATION",
      "INIT_BY_ID",
    ]),

    ...mapActions("admin-application", [
      "INIT_EDUCATION_FACULTYS",
      "INIT_EDUCATION_TYPES",
      "INIT_EDUCATION_LANGUAGES",
      "INIT_COUNTRYS",
      "INIT_REGIONS",
      "INIT_CITYS",
      "SET_APPLICATION",
    ]),

    /* Case-insensitive name filter shared by the address dropdowns. */
    filterByName(list, query) {
      if (!query) return list;
      const q = query.toLowerCase();
      return list.filter((x) => (x.name || "").toLowerCase().includes(q));
    },

    /* Resolve a dropdown's label: prefer the loaded list, then the value saved on the
     * application (list still loading, or the entry is no longer returned), then "—". */
    pickedName(list, selectedId, saved) {
      const hit = list.find((x) => x.id == selectedId);
      if (hit) return hit.name;
      if (saved && saved.id && saved.id == selectedId && saved.name) return saved.name;
      return "—";
    },

    /* Fetch Mamlakat/Viloyat/Tuman lists for the given selection. INIT_COUNTRYS wipes
     * the region + city lists in the store, so the three run in order, not in parallel. */
    async loadAddressLists(countryId, regionId) {
      this.loadingRegions = true;
      this.loadingCities = true;
      try {
        await this.INIT_COUNTRYS();
        if (countryId) await this.INIT_REGIONS({ countryId });
        if (countryId && regionId) await this.INIT_CITYS({ regionId });
      } finally {
        this.loadingRegions = false;
        this.loadingCities = false;
      }
    },

    async onSelectCountry(country) {
      this.editForm.countryId = country.id;
      // Regions belong to a country and districts to a region — a country change
      // invalidates both, so wipe them and re-collect from the fresh list.
      this.editForm.regionId = null;
      this.editForm.cityId = null;
      this.countryDropOpen = false;
      this.countrySearch = "";

      this.loadingRegions = true;
      await this.INIT_REGIONS({ countryId: country.id });
      this.loadingRegions = false;
    },

    async onSelectRegion(region) {
      this.editForm.regionId = region.id;
      this.editForm.cityId = null;
      this.regionDropOpen = false;
      this.regionSearch = "";

      this.loadingCities = true;
      await this.INIT_CITYS({ regionId: region.id });
      this.loadingCities = false;
    },

    onSelectCity(city) {
      this.editForm.cityId = city.id;
      this.cityDropOpen = false;
      this.citySearch = "";
    },

    getGender(id) {
      return this.genders.find((x) => x.id == id) || {};
    },

    /* The +998 phone mask uses autoUnmask, so it edits only the 9 subscriber
     * digits and drops the "+998" literal. The API (and the $tel filter /
     * <tel-input>) use the full "998XXXXXXXXX" form, so convert at the form
     * boundary: strip the country code into the form, re-attach it on save. */
    toSubscriber(v) {
      const d = String(v || "").replace(/\D/g, "");
      if (d.length === 12 && d.startsWith("998")) return d.slice(3);
      if (d.length > 9) return d.slice(-9);
      return d;
    },
    toFullPhone(v) {
      const d = String(v || "").replace(/\D/g, "");
      if (!d) return null;
      return d.length === 9 ? `998${d}` : d;
    },

    /* Personal phone: tell the admin the number is taken WHILE they type, instead of only as a
     * red toast after Saqlash. The server answers with the same rule that rejects the save
     * (@ValidUniqueMobilePhone → checkMobilePhoneForDuplicate), so the two can't disagree.
     * Debounced, and only asked once all 9 subscriber digits are in. */
    queuePhoneDuplicateCheck(value) {
      clearTimeout(this.phoneDuplicateTimer);
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length !== 9 || !this.application || !this.application.id) {
        this.phoneDuplicate = false;
        return;
      }
      this.phoneDuplicateTimer = setTimeout(() => this.checkPhoneDuplicate(digits), 400);
    },

    async checkPhoneDuplicate(digits) {
      // silent: a failed check must not raise the global error toast — it just leaves the
      // warning off, and the save-time guard still catches the duplicate.
      const res = await this.$axios.$get("admin/application/check-mobile-phone", {
        params: { phone: this.toFullPhone(digits), excludeId: this.application.id },
        silent: true,
      });
      // Ignore an answer that arrived after the admin typed on — it describes an older value.
      if (this.toSubscriber(this.editForm.mobilePhone) !== digits) return;
      this.phoneDuplicate = res != null && res.exists === true;
    },
    // Format a stored phone for display as "+998 XX XXX XX XX" (same treatment
    // as the contract 2026 show page). Falls back to the raw value if it can't
    // be parsed into the expected shape.
    formatPhone(phone) {
      if (!phone) return "";
      let digits = String(phone).replace(/\D/g, "");
      if (digits.startsWith("998")) {
        digits = "+" + digits;
      } else if (digits.length === 9 && digits.startsWith("9")) {
        digits = "+998" + digits;
      }
      const match = digits.match(/^\+998(\d{2})(\d{3})(\d{2})(\d{2})$/);
      if (match) {
        return `+998 ${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
      }
      return digits || String(phone);
    },
    // Normalize a stored date (dd.MM.yyyy from check-application, or ISO) to the
    // yyyy-MM-dd that the DatePicker component requires.
    toDateInput(v) {
      if (!v) return "";
      const s = String(v);
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
      const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
    },

    async startEdit() {
      this.typeAbiturientChanged = false;
      this.phoneDuplicate = false;
      const a = this.application;
      this.SET_APPLICATION(a);
      this.editForm = {
        firstName: a.firstName || "",
        lastName: a.lastName || "",
        middleName: a.middleName || "",
        passportNumber: a.passportNumber || "",
        jshir: a.jshir || "",
        mobilePhone: this.toSubscriber(a.mobilePhone),
        additionalPhone: this.toSubscriber(a.additionalPhone),
        homePhone: this.toSubscriber(a.homePhone),
        birthDate: this.toDateInput(a.birthDate),
        gender: a.gender || "",
        fatherPhone: this.toSubscriber(a.fatherPhone),
        motherPhone: this.toSubscriber(a.motherPhone),
        email: a.email || "",
        addressLine: a.addressLine || "",
        telegram: a.telegram || "",
        countryId: a.country?.id || null,
        regionId: a.region?.id || null,
        cityId: a.city?.id || null,
        nationality: a.nationality || "",
        educationFacultyId: a.educationFaculty?.id || null,
        educationTypeId: a.educationType?.id || null,
        educationLanguageId: a.educationLanguage?.id || null,
        examType: a.examType,
        dtmScore: a.dtmScore || "",
        dtmAbiturientNumber: a.dtmAbiturientNumber || "",
        ball: a.ball || "",
        stajYear: a.stajYear || "",
        nameOTM: a.nameOTM || "",
        nameYonalish: a.nameYonalish || "",
        finishKurs: a.finishKurs || "",
        typeAbiturient: a.typeAbiturient || "",
      };
      this.isEditing = true;

      // Address dropdown data (Mamlakat → Viloyat → Tuman). Only the levels below the
      // saved value are fetched — the region/city lists are keyed by the level above.
      await this.loadAddressLists(this.editForm.countryId, this.editForm.regionId);

      // Load education dropdown data — Magistratura shows only master directions, and
      // pricedOnly keeps the list to directions with a 1-kurs price (like qabul.usat.uz).
      await this.INIT_EDUCATION_FACULTYS({
        master: a.typeAbiturient === "MASTER",
        pricedOnly: true,
      });
      if (a.educationFaculty?.id) {
        await this.INIT_EDUCATION_TYPES({ id: a.educationFaculty.id });
        await this.loadExamTypeVisibility(a.educationFaculty.id);
      }
      if (a.educationType?.id && a.educationFaculty?.id) {
        await this.INIT_EDUCATION_LANGUAGES({
          typeId: a.educationType.id,
          facultyId: a.educationFaculty.id,
        });
      }
    },

    cancelEdit() {
      this.clearEditDraft();
      this.isEditing = false;
      this.editForm = {};
      clearTimeout(this.phoneDuplicateTimer);
      this.phoneDuplicate = false;
      this.closeAllDropdowns();
    },

    toggleDropdown(name) {
      const wasOpen = this[name];
      this.closeAllDropdowns();
      if (!wasOpen) this[name] = true;
    },

    closeAllDropdowns() {
      this.facultyDropOpen = false;
      this.typeDropOpen = false;
      this.languageDropOpen = false;
      this.examDropOpen = false;
      this.typeAbitDropOpen = false;
      this.countryDropOpen = false;
      this.regionDropOpen = false;
      this.cityDropOpen = false;
      this.facultySearch = "";
      this.countrySearch = "";
      this.regionSearch = "";
      this.citySearch = "";
    },

    onSelectTypeAbit(value) {
      this.editForm.typeAbiturient = value;
      this.typeAbitDropOpen = false;
      // Mirror the old <select> @change: a Hujjat turi change wipes the chosen
      // Ta'lim yo'nalishi / type / language and reloads the matching direction list.
      this.onTypeAbiturientChange();
    },

    async onSelectFaculty(faculty) {
      this.editForm.educationFacultyId = faculty.id;
      this.editForm.educationTypeId = null;
      this.editForm.educationLanguageId = null;
      this.facultyDropOpen = false;
      this.facultySearch = "";

      this.loadingTypes = true;
      await Promise.all([
        this.INIT_EDUCATION_TYPES({ id: faculty.id }),
        this.loadExamTypeVisibility(faculty.id),
      ]);
      this.loadingTypes = false;
    },

    // Fetch the faculty's exam-config (Imtihon sozlamalari) so displayTypes can
    // restrict ABITURIENT type options to allowedEducationTypeIds. Silent +
    // fail-open: a faculty with no config (null/404) leaves examTypeVisibility
    // null and every offered type stays visible.
    async loadExamTypeVisibility(facultyId) {
      this.examTypeVisibility = null;
      if (!facultyId) return;
      try {
        const data = await this.$axios.$get(`admin/exam-config/faculty/${facultyId}`, { silent: true });
        this.examTypeVisibility = data || null;
      } catch {
        this.examTypeVisibility = null;
      }
    },

    async onSelectType(type) {
      this.editForm.educationTypeId = type.id;
      this.editForm.educationLanguageId = null;
      this.typeDropOpen = false;

      this.loadingLanguages = true;
      await this.INIT_EDUCATION_LANGUAGES({
        typeId: type.id,
        facultyId: this.editForm.educationFacultyId,
      });
      this.loadingLanguages = false;
    },

    onSelectLanguage(lang) {
      this.editForm.educationLanguageId = lang.id;
      this.languageDropOpen = false;
    },

    onTypeAbiturientChange() {
      // Magistratura / Abituriyent / etc. offer different Ta'lim yo'nalishi lists, so a
      // Hujjat turi change invalidates any chosen faculty/type/language — wipe them and
      // require a fresh selection before the edit can be saved.
      this.typeAbiturientChanged = true;
      this.editForm.educationFacultyId = null;
      this.editForm.educationTypeId = null;
      this.editForm.educationLanguageId = null;
      // Reload the direction list for the new Hujjat turi: Magistratura → master
      // directions only, and always only 1-kurs-priced directions, matching qabul.usat.uz.
      this.INIT_EDUCATION_FACULTYS({
        master: this.editForm.typeAbiturient === "MASTER",
        pricedOnly: true,
      });
    },

    async saveEdit() {
      this.isSaving = true;
      try {
        // Merge edits into the full application object (API expects full ApplicationDTO)
        const app = { ...this.application };
        // Helper: empty string → null (prevents Jackson enum/date parse errors)
        const orNull = (v) => (v === "" || v == null) ? null : v;

        app.firstName = orNull(this.editForm.firstName);
        app.lastName = orNull(this.editForm.lastName);
        app.middleName = orNull(this.editForm.middleName);
        app.passportNumber = orNull(this.editForm.passportNumber);
        app.jshir = orNull(this.editForm.jshir);
        app.mobilePhone = this.toFullPhone(this.editForm.mobilePhone);
        app.additionalPhone = this.toFullPhone(this.editForm.additionalPhone);
        app.homePhone = this.toFullPhone(this.editForm.homePhone);
        app.birthDate = orNull(this.editForm.birthDate);
        app.gender = orNull(this.editForm.gender);
        app.fatherPhone = this.toFullPhone(this.editForm.fatherPhone);
        app.motherPhone = this.toFullPhone(this.editForm.motherPhone);
        app.email = orNull(this.editForm.email);
        app.addressLine = orNull(this.editForm.addressLine);
        app.nationality = orNull(this.editForm.nationality);
        app.examType = this.editForm.examType;
        app.dtmScore = orNull(this.editForm.dtmScore);
        app.dtmAbiturientNumber = orNull(this.editForm.dtmAbiturientNumber);
        app.ball = orNull(this.editForm.ball);
        app.stajYear = orNull(this.editForm.stajYear);
        app.nameOTM = orNull(this.editForm.nameOTM);
        app.nameYonalish = orNull(this.editForm.nameYonalish);
        app.finishKurs = orNull(this.editForm.finishKurs);
        // Document type (Hujjat turi) is editable only among the special application
        // kinds; mirror the backend guard so a regular edit can never flip the type.
        if (["ABITURIENT", "MASTER", "PEREVOD", "SECOND_SPES"].includes(this.editForm.typeAbiturient)) {
          app.typeAbiturient = this.editForm.typeAbiturient;
        }

        // Address fields use IdNameDTO { id, name }. Unlike the education block below,
        // these are assigned unconditionally: a country change wipes region + city, and
        // the cleared levels must reach the server as null so the old ones don't stick.
        const idName = (list, id, saved) => {
          if (!id) return null;
          const name = this.pickedName(list, id, saved);
          return { id, name: name === "—" ? "" : name };
        };
        app.country = idName(this.countries, this.editForm.countryId, this.application.country);
        app.region = idName(this.regions, this.editForm.regionId, this.application.region);
        app.city = idName(this.cities, this.editForm.cityId, this.application.city);

        // Education fields use IdNameDTO { id, name }
        if (this.editForm.educationFacultyId) {
          const f = this.educationFaculties.find((x) => x.id == this.editForm.educationFacultyId);
          app.educationFaculty = { id: this.editForm.educationFacultyId, name: f?.name || "" };
        }
        if (this.editForm.educationTypeId) {
          const t = this.displayTypes.find((x) => x.id == this.editForm.educationTypeId);
          app.educationType = { id: this.editForm.educationTypeId, name: t?.name || "" };
        }
        if (this.editForm.educationLanguageId) {
          const l = this.displayLanguages.find((x) => x.id == this.editForm.educationLanguageId);
          app.educationLanguage = { id: this.editForm.educationLanguageId, name: l?.name || "" };
        }

        const res = await this.$axios.$post("admin/application", app);
        if (res != null) {
          // Reload the application to reflect saved changes
          await this.INIT_BY_ID({ id: this.application.id });
          this.clearEditDraft();
          this.isEditing = false;
          this.typeAbiturientChanged = false;
        }
      } catch (e) {
        console.error("Save failed:", e);
      }
      this.isSaving = false;
    },

    onAccept() {
      this.ACCEPT_APPLICATION({ id: this.application.id }).then((status) => {
        if (status) {
          window.location.reload();
        }
      });
    },

    onPassed() {
      this.PASSED_APPLICATION({ id: this.application.id }).then((status) => {
        if (status) {
          window.location.reload();
        }
      });
    },

    onSuggested() {
      this.SUGGESTED_APPLICATION({ id: this.application.id }).then((status) => {
        if (status) {
          window.location.reload();
        }
      });
    },

    onPaid() {
      this.PAID_APPLICATION({ id: this.application.id }).then((status) => {
        if (status) {
          window.location.reload();
        }
      });
    },

    onRejects() {
      const params = {
        id: this.application.id,
        reviews: this.application.reviews,
      };
      this.REJECT_APPLICATION(params).then((status) => {
        if (status) {
          window.location.reload();
        }
      });
    },
  },
};
</script>

<template>
  <div :class="['app-show-content', { 'app-show-content--dark': isDark }]">
    <!-- Where this record came from. Only shown for the DTM xlsx import, because everything else is
         either a self-registration or already described by the source/filledBy fields below. -->
    <div v-if="isExcelImported" class="excel-pill">
      <v-icon size="15" color="#fff" class="mr-1">mdi-microsoft-excel</v-icon>
      {{ $t('EXCEL ORQALI YUKLANGAN') }}
    </div>
    <v-form
      :readonly="isReadonly"
      @submit.prevent="() => false"
      ref="form"
      lazy-validation
    >
      <!-- ═══ EDIT MODE ═══ -->
      <template v-if="isEditing">
        <v-row>
          <v-col cols="12" md="6">
            <div class="ef-group">
              <div class="ef">
                <label class="ef__label">{{ $t('First name') }}</label>
                <input
                  v-model="editForm.firstName"
                  v-inputmask="$masks.text"
                  :class="['ef__input', { 'ef__input--error': fieldErrors.firstName }]"
                  type="text"
                  maxlength="80"
                  :title="$t('Use only english letters')"
                />
              </div>
              <div class="ef">
                <label class="ef__label">{{ $t('Last name') }}</label>
                <input
                  v-model="editForm.lastName"
                  v-inputmask="$masks.text"
                  :class="['ef__input', { 'ef__input--error': fieldErrors.lastName }]"
                  type="text"
                  maxlength="80"
                  :title="$t('Use only english letters')"
                />
              </div>
              <div class="ef">
                <label class="ef__label">{{ $t('Middle name') }}</label>
                <input
                  v-model="editForm.middleName"
                  v-inputmask="$masks.text"
                  :class="['ef__input', { 'ef__input--error': fieldErrors.middleName }]"
                  type="text"
                  maxlength="80"
                  :title="$t('Use only english letters')"
                />
              </div>
              <div class="ef">
                <label class="ef__label">{{ $t('Passport series and number') }}</label>
                <input
                  v-model="editForm.passportNumber"
                  v-inputmask="$masks.passport"
                  :class="['ef__input', { 'ef__input--error': fieldErrors.passportNumber }]"
                  type="text"
                  placeholder="AA 1234567"
                  :title="$t('Passport series not valid')"
                />
              </div>
              <div class="ef">
                <label class="ef__label">{{ $t('jshir') }}</label>
                <input
                  v-model="editForm.jshir"
                  v-inputmask="$masks.numberLength12"
                  :class="['ef__input', { 'ef__input--error': fieldErrors.jshir }]"
                  type="text"
                  inputmode="numeric"
                />
              </div>
              <div class="ef">
                <label class="ef__label">{{ $t('Date of birth') }}</label>
                <DatePicker
                  :value="editForm.birthDate"
                  @input="editForm.birthDate = $event"
                  max="2030-12-31"
                  class="ef__datepicker"
                />
              </div>
              <div class="ef">
                <label class="ef__label">{{ $t('Personal phone number') }}</label>
                <input
                  v-model="editForm.mobilePhone"
                  v-inputmask="$masks.phone"
                  :class="['ef__input', { 'ef__input--error': fieldErrors.mobilePhone }]"
                  type="tel"
                  inputmode="numeric"
                  placeholder="+998 90 123 45 67"
                />
                <div v-if="phoneDuplicate" class="ef__error-text">
                  {{ $t('This phone number already exists') }}
                </div>
              </div>
              <div class="ef">
                <label class="ef__label">{{ $t('add phone number') }}</label>
                <input
                  v-model="editForm.additionalPhone"
                  v-inputmask="$masks.phone"
                  :class="['ef__input', { 'ef__input--error': fieldErrors.additionalPhone }]"
                  type="tel"
                  inputmode="numeric"
                  placeholder="+998 90 123 45 67"
                />
              </div>
              <div class="ef">
                <label class="ef__label">{{ $t('Home phone number') }}</label>
                <input
                  v-model="editForm.homePhone"
                  v-inputmask="$masks.phone"
                  :class="['ef__input', { 'ef__input--error': fieldErrors.homePhone }]"
                  type="tel"
                  inputmode="numeric"
                  placeholder="+998 90 123 45 67"
                />
              </div>
              <div class="ef">
                <label class="ef__label">{{ $t('Father phone') }}</label>
                <input
                  v-model="editForm.fatherPhone"
                  v-inputmask="$masks.phone"
                  :class="['ef__input', { 'ef__input--error': fieldErrors.fatherPhone }]"
                  type="tel"
                  inputmode="numeric"
                  placeholder="+998 90 123 45 67"
                />
              </div>
              <div class="ef">
                <label class="ef__label">{{ $t('Mother phone') }}</label>
                <input
                  v-model="editForm.motherPhone"
                  v-inputmask="$masks.phone"
                  :class="['ef__input', { 'ef__input--error': fieldErrors.motherPhone }]"
                  type="tel"
                  inputmode="numeric"
                  placeholder="+998 90 123 45 67"
                />
              </div>
              <div class="ef">
                <label class="ef__label">{{ $t('Email') }}</label>
                <input v-model="editForm.email" class="ef__input" type="email" maxlength="120" />
              </div>

              <!-- Mamlakat → Viloyat → Tuman. Cascading, same as Ta'lim yo'nalishi:
                   each level is disabled until the level above it is chosen. -->
              <div class="ef">
                <label class="ef__label">{{ $t('Country') }}</label>
                <div class="ef-dropdown" :class="{ 'ef-dropdown--open': countryDropOpen }">
                  <button type="button" class="ef-dropdown__trigger" @click="toggleDropdown('countryDropOpen')">
                    <span :class="{ 'ef-dropdown__placeholder': !editForm.countryId }">{{ selectedCountryName }}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  <div v-if="countryDropOpen" class="ef-dropdown__menu ef-dropdown__menu--tall">
                    <div class="ef-dropdown__search">
                      <input
                        v-model="countrySearch"
                        class="ef-dropdown__search-input"
                        type="text"
                        :placeholder="$t('Search') + '...'"
                        @click.stop
                      />
                    </div>
                    <div class="ef-dropdown__scroll">
                      <div
                        v-for="c in filteredCountries"
                        :key="c.id"
                        class="ef-dropdown__item"
                        :class="{ 'ef-dropdown__item--active': editForm.countryId == c.id }"
                        @click="onSelectCountry(c)"
                      >{{ c.name }}</div>
                      <div v-if="!filteredCountries.length" class="ef-dropdown__empty">—</div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="ef">
                <label class="ef__label">{{ $t('Region') }}</label>
                <div class="ef-dropdown" :class="{ 'ef-dropdown--open': regionDropOpen }">
                  <button type="button" class="ef-dropdown__trigger" :disabled="!editForm.countryId || loadingRegions" @click="toggleDropdown('regionDropOpen')">
                    <span :class="{ 'ef-dropdown__placeholder': !editForm.regionId }">{{ loadingRegions ? '...' : selectedRegionName }}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  <div v-if="regionDropOpen" class="ef-dropdown__menu ef-dropdown__menu--tall">
                    <div class="ef-dropdown__search">
                      <input
                        v-model="regionSearch"
                        class="ef-dropdown__search-input"
                        type="text"
                        :placeholder="$t('Search') + '...'"
                        @click.stop
                      />
                    </div>
                    <div class="ef-dropdown__scroll">
                      <div
                        v-for="r in filteredRegions"
                        :key="r.id"
                        class="ef-dropdown__item"
                        :class="{ 'ef-dropdown__item--active': editForm.regionId == r.id }"
                        @click="onSelectRegion(r)"
                      >{{ r.name }}</div>
                      <div v-if="!filteredRegions.length" class="ef-dropdown__empty">—</div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="ef">
                <label class="ef__label">{{ $t('City / District') }}</label>
                <div class="ef-dropdown" :class="{ 'ef-dropdown--open': cityDropOpen }">
                  <button type="button" class="ef-dropdown__trigger" :disabled="!editForm.regionId || loadingCities" @click="toggleDropdown('cityDropOpen')">
                    <span :class="{ 'ef-dropdown__placeholder': !editForm.cityId }">{{ loadingCities ? '...' : selectedCityName }}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  <div v-if="cityDropOpen" class="ef-dropdown__menu ef-dropdown__menu--tall">
                    <div class="ef-dropdown__search">
                      <input
                        v-model="citySearch"
                        class="ef-dropdown__search-input"
                        type="text"
                        :placeholder="$t('Search') + '...'"
                        @click.stop
                      />
                    </div>
                    <div class="ef-dropdown__scroll">
                      <div
                        v-for="c in filteredCities"
                        :key="c.id"
                        class="ef-dropdown__item"
                        :class="{ 'ef-dropdown__item--active': editForm.cityId == c.id }"
                        @click="onSelectCity(c)"
                      >{{ c.name }}</div>
                      <div v-if="!filteredCities.length" class="ef-dropdown__empty">—</div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="ef">
                <label class="ef__label">{{ $t('Nationality') }}</label>
                <input v-model="editForm.nationality" class="ef__input" type="text" maxlength="100" />
              </div>

              <div class="ef">
                <label class="ef__label">{{ $t('Address') }}</label>
                <input
                  v-model="editForm.addressLine"
                  class="ef__input"
                  type="text"
                  maxlength="255"
                  :placeholder="$t('Enter the address ...')"
                />
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="ef-group">
              <!-- Academic direction (Faculty) -->
              <div class="ef">
                <label class="ef__label">{{ $t('Field of study') }}</label>
                <div class="ef-dropdown" :class="{ 'ef-dropdown--open': facultyDropOpen }">
                  <button type="button" class="ef-dropdown__trigger" @click="toggleDropdown('facultyDropOpen')">
                    <span :class="{ 'ef-dropdown__placeholder': !editForm.educationFacultyId }">{{ selectedFacultyName }}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  <div v-if="facultyDropOpen" class="ef-dropdown__menu ef-dropdown__menu--tall">
                    <div class="ef-dropdown__search">
                      <input
                        v-model="facultySearch"
                        class="ef-dropdown__search-input"
                        type="text"
                        :placeholder="$t('Search') + '...'"
                        @click.stop
                      />
                    </div>
                    <div class="ef-dropdown__scroll">
                      <div
                        v-for="f in filteredFaculties"
                        :key="f.id"
                        class="ef-dropdown__item"
                        :class="{ 'ef-dropdown__item--active': editForm.educationFacultyId == f.id }"
                        @click="onSelectFaculty(f)"
                      >{{ f.name }}</div>
                      <div v-if="!filteredFaculties.length" class="ef-dropdown__empty">—</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Type of education -->
              <div class="ef">
                <label class="ef__label">{{ $t('Type of education') }}</label>
                <div class="ef-dropdown" :class="{ 'ef-dropdown--open': typeDropOpen }">
                  <button type="button" class="ef-dropdown__trigger" :disabled="!editForm.educationFacultyId || loadingTypes" @click="toggleDropdown('typeDropOpen')">
                    <span :class="{ 'ef-dropdown__placeholder': !editForm.educationTypeId }">{{ loadingTypes ? '...' : selectedTypeName }}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  <div v-if="typeDropOpen && displayTypes.length" class="ef-dropdown__menu">
                    <div
                      v-for="t in displayTypes"
                      :key="t.id"
                      class="ef-dropdown__item"
                      :class="{ 'ef-dropdown__item--active': editForm.educationTypeId == t.id }"
                      @click="onSelectType(t)"
                    >{{ t.name }}</div>
                  </div>
                </div>
              </div>

              <!-- Language of education -->
              <div class="ef">
                <label class="ef__label">{{ $t('Language of instruction') }}</label>
                <div class="ef-dropdown" :class="{ 'ef-dropdown--open': languageDropOpen }">
                  <button type="button" class="ef-dropdown__trigger" :disabled="!editForm.educationTypeId || loadingLanguages" @click="toggleDropdown('languageDropOpen')">
                    <span :class="{ 'ef-dropdown__placeholder': !editForm.educationLanguageId }">{{ loadingLanguages ? '...' : selectedLanguageName }}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  <div v-if="languageDropOpen && displayLanguages.length" class="ef-dropdown__menu">
                    <div
                      v-for="l in displayLanguages"
                      :key="l.id"
                      class="ef-dropdown__item"
                      :class="{ 'ef-dropdown__item--active': editForm.educationLanguageId == l.id }"
                      @click="onSelectLanguage(l)"
                    >{{ l.name }}</div>
                  </div>
                </div>
              </div>
              <div class="ef">
                <label class="ef__label">{{ $t('Choose exam type passed') }}</label>
                <div class="ef-dropdown" :class="{ 'ef-dropdown--open': examDropOpen }">
                  <button type="button" class="ef-dropdown__trigger" @click="toggleDropdown('examDropOpen')">
                    <span>{{ editForm.examType ? 'Online' : 'Offline' }}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  <div v-if="examDropOpen" class="ef-dropdown__menu">
                    <div class="ef-dropdown__item" :class="{ 'ef-dropdown__item--active': editForm.examType === true }" @click="editForm.examType = true; examDropOpen = false">Online</div>
                    <div class="ef-dropdown__item" :class="{ 'ef-dropdown__item--active': editForm.examType === false }" @click="editForm.examType = false; examDropOpen = false">Offline</div>
                  </div>
                </div>
              </div>
              <!-- "Imtihon natijasi" (ball) is intentionally NOT editable here — the input was
                   removed per request. editForm.ball still round-trips the stored score on save. -->
              <template v-if="isStaj">
                <div class="ef">
                  <label class="ef__label">{{ $t('Ish Staji') }}</label>
                  <input v-model="editForm.stajYear" class="ef__input" type="text" />
                </div>
              </template>
              <!-- Hujjat turi (document type): editable for EVERY intake, incl. ABITURIENT. -->
              <div class="ef">
                <label class="ef__label">{{ $t('Hujjat type') }}</label>
                <div class="ef-dropdown" :class="{ 'ef-dropdown--open': typeAbitDropOpen }">
                  <button type="button" class="ef-dropdown__trigger" @click="toggleDropdown('typeAbitDropOpen')">
                    <span :class="{ 'ef-dropdown__placeholder': !editForm.typeAbiturient }">{{ selectedTypeAbitLabel }}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  <div v-if="typeAbitDropOpen" class="ef-dropdown__menu">
                    <div
                      v-for="opt in typeAbitOptions"
                      :key="opt.value"
                      class="ef-dropdown__item"
                      :class="{ 'ef-dropdown__item--active': editForm.typeAbiturient === opt.value }"
                      @click="onSelectTypeAbit(opt.value)"
                    >{{ opt.label }}</div>
                  </div>
                </div>
              </div>
              <!-- Prior-study fields apply only to transfer / master / 2nd-speciality. -->
              <template v-if="editForm.typeAbiturient == 'PEREVOD' || editForm.typeAbiturient == 'SECOND_SPES' || editForm.typeAbiturient == 'MASTER'">
                <div class="ef">
                  <label class="ef__label">{{ $t('OTM nomi') }}</label>
                  <input v-model="editForm.nameOTM" class="ef__input" type="text" />
                </div>
                <div class="ef">
                  <label class="ef__label">{{ $t('O`qigan ta`lim yo`nalishi') }}</label>
                  <input v-model="editForm.nameYonalish" class="ef__input" type="text" />
                </div>
                <div v-if="editForm.typeAbiturient == 'PEREVOD'" class="ef">
                  <label class="ef__label">{{ $t('finish kurs') }}</label>
                  <input v-model="editForm.finishKurs" class="ef__input" type="text" />
                </div>
              </template>
            </div>

            <!-- Photos (editable) -->
            <div class="ef-photos-edit">
              <div class="ef-photo-item">
                <label class="ef__label">{{ $t('Photo') }}</label>
                <upload-file-admin param="photo" />
              </div>
              <div class="ef-photo-item">
                <label class="ef__label">{{ $t('Upload a copy of your passport') }}</label>
                <upload-file-admin param="passportPhoto" />
              </div>
              <div class="ef-photo-item">
                <label class="ef__label">{{ $t('Upload a copy of your passport back') }}</label>
                <upload-file-admin param="passportBackPhoto" />
              </div>
              <div v-if="isStaj" class="ef-photo-item">
                <label class="ef__label">{{ $t('staj_rasm') }}</label>
                <upload-file-admin param="stajPhoto" />
              </div>
              <div v-if="application.typeAbiturient == 'PEREVOD'" class="ef-photo-item">
                <label class="ef__label">{{ $t('Transkript') }}</label>
                <upload-file-admin param="transcriptPhoto" />
              </div>
              <div v-else-if="application.typeAbiturient == 'MASTER' || application.typeAbiturient == 'SECOND_SPES'" class="ef-photo-item">
                <label class="ef__label">{{ $t('Diplom') }}</label>
                <upload-file-admin param="highSchoolDiplomaIlovaPhoto" />
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- DTM on its own full-width row, below the two-column layout. In the right-hand
             column it was squeezed to half width, which left the score and applicant id
             cramped and the result sheet narrower than every other document on the form. -->
        <v-row>
          <v-col cols="12">
              <!-- DTM block, kept together and visually separated from the exam fields above.
                   The score and applicant id previously sat loose among them under the generic
                   labels "Total score" / "Applicant ID number", where nothing said they were DTM
                   and they read as the entrance-exam score. The result sheet is re-uploadable
                   here: it is the evidence behind a waived entrance exam, so staff must be able
                   to replace an unreadable scan without sending the applicant back to re-register. -->
              <div class="ef-dtm">
                <div class="ef-dtm__title">{{ $t('DTM result') }}</div>
                <div class="ef-dtm__grid">
                  <div class="ef">
                    <label class="ef__label">{{ $t('DTM result') }}</label>
                    <input v-model="editForm.dtmScore" class="ef__input" type="text" />
                  </div>
                  <div class="ef">
                    <label class="ef__label">{{ $t('Applicant dtm ID number') }}</label>
                    <input v-model="editForm.dtmAbiturientNumber" class="ef__input" type="text" />
                  </div>
                </div>
                <div class="ef-photo-item ef-dtm__file">
                  <label class="ef__label">{{ $t('DTM Test Paper Result') }}</label>
                  <upload-file-admin param="dtmAbiturientPhoto" />
                </div>
              </div>
          </v-col>
        </v-row>
      </template>

      <!-- ═══ READ MODE ═══ -->
      <template v-else>
        <v-row>
          <v-col cols="12" md="6">
            <div class="info-section">
              <admin-app-show-detail
                :title="$t('First name')"
                :text="application.firstName"
                v-model="application.reviews.firstName"
              />
              <admin-app-show-detail
                class="pt-4"
                :title="$t('Last name')"
                :text="application.lastName"
                v-model="application.reviews.lastName"
              />
              <admin-app-show-detail
                class="pt-4"
                :title="$t('Middle name')"
                :text="application.middleName"
                v-model="application.reviews.middleName"
              />
              <admin-app-show-detail
                class="pt-4"
                :title="$t('Passport series and number')"
                :text="application.passportNumber"
                v-model="application.reviews.passportNumber"
              />
              <copiable-text
                class="pt-4"
                :label="$t('jshir')"
                :text="application.jshir"
              />
              <copiable-text
                class="pt-4"
                :label="$t('Personal phone number')"
                :text="formatPhone(application.mobilePhone)"
              />
              <copiable-text
                class="pt-4"
                :label="$t('add phone number')"
                :text="formatPhone(application.additionalPhone)"
              />
              <copiable-text
                v-if="application.plainPassword"
                class="pt-4"
                :label="$t('Password')"
                :text="application.plainPassword"
              />
              <admin-app-show-detail
                class="pt-4"
                :title="$t('Date of birth')"
                :text="application.birthDate"
              />
              <br />
              <div>
                <div class="detail-title" v-text="$t('Country')" />
                <div class="detail-text" v-text="application.country.name" />
              </div>
              <div class="pt-4">
                <div class="detail-title" v-text="$t('Region')" />
                <div class="detail-text" v-text="application.region.name" />
              </div>
              <div class="pt-4">
                <div class="detail-title" v-text="$t('City / District')" />
                <div class="detail-text" v-text="application.city.name" />
              </div>
              <div class="pt-4" v-if="application.nationality">
                <div class="detail-title" v-text="$t('Nationality')" />
                <div class="detail-text" v-text="application.nationality" />
              </div>
              <div class="pt-4" v-if="application.addressLine">
                <div class="detail-title" v-text="$t('Address')" />
                <div class="detail-text" v-text="application.addressLine" />
              </div>
              <div class="pt-4" v-if="application.bitrixSourceId">
                <div class="detail-title" v-text="$t('Source')" />
                <div class="detail-text" v-text="sourceLabel(application.bitrixSourceId)" />
              </div>
              <div class="pt-4">
                <div class="detail-title" v-text="$t('Filled by')" />
                <div class="detail-text" v-text="application.filledBy || $t('The student themselves')" />
              </div>
              <!-- DTM score / applicant id used to be duplicated here, in the middle of the
                   personal-details column. They now live in the dedicated DTM section under
                   the education block, together with the uploaded DTM sheet. -->
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="info-section">
              <admin-app-show-detail
                v-if="application.passportPhoto"
                :title="$t('Copy of the passport')"
                v-model="application.reviews.passportPhoto"
              />
              <admin-app-show-photo
                class="pt-2"
                v-if="application.photo"
                :url="$file(application.photo)"
              />
              <admin-app-show-photo
                class="pt-2"
                v-if="application.passportPhoto"
                :url="$file(application.passportPhoto)"
              />
              <br />
              <admin-app-show-photo
                class="pt-2"
                v-if="application.passportBackPhoto"
                :url="$file(application.passportBackPhoto)"
              />
            </div>
          </v-col>
        </v-row>

        <!-- Gradient divider -->
        <div class="section-gap">
          <div class="gradient-divider" />
        </div>

        <!-- Education info -->
        <v-row>
          <v-col cols="12" md="6">
            <div class="info-section">
              <admin-app-show-detail
                :title="$t('Field of study')"
                class="pt-4"
                :text="application.educationFaculty ? application.educationFaculty.name : ''"
                v-model="application.reviews.educationFaculty"
              />
              <copiable-text
                class="pt-4"
                :label="$t('Field of study code')"
                :text="application.facultyMvdir || ''"
              />
              <admin-app-show-detail
                :title="$t('Type of education')"
                class="pt-4"
                :text="application.educationType ? application.educationType.name : ''"
                v-model="application.reviews.educationType"
              />
              <admin-app-show-detail
                :title="$t('Language of instruction')"
                class="pt-4"
                :text="application.educationLanguage ? application.educationLanguage.name : ''"
                v-model="application.reviews.educationLanguage"
              />
              <div class="pt-4">
                <admin-app-show-detail
                  :title="$t('Choose exam type passed')"
                  class="pt-4"
                  :text="application.examType ? 'Online' : 'Offline'"
                />
              </div>
              <div
                class="pt-4"
                v-if="(isSuperAdmin || isQabulQiluvchi || canUpdate) && application.passedDate"
              >
                <admin-app-show-detail
                  :title="$t('test topshirgan kuni')"
                  class="pt-4"
                  :text="application.passedDate"
                />
              </div>
              <div
                class="pt-4"
                v-if="(isSuperAdmin || isQabulQiluvchi || canUpdate) && application.ball"
              >
                <admin-app-show-detail
                  :title="$t('ball_exam')"
                  class="pt-4"
                  :text="application.ball"
                />
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6" v-if="isStaj">
            <div class="info-section">
              <admin-app-show-detail
                :title="$t('Ish Staji')"
                class="pt-4"
                :text="application.stajYear"
                v-model="application.reviews.stajYear"
              />
              <br />
              <admin-app-show-detail
                :title="$t('Ish Staji to`g`risidagi ma`lumot')"
                v-model="application.reviews.stajPhoto"
              />
              <admin-app-show-photo
                class="pt-2"
                v-if="application.stajPhoto"
                :url="$file(application.stajPhoto)"
              />
            </div>
          </v-col>
        </v-row>

        <!-- ═══ DTM ═══
             Directly beneath the education / results block. Shown when the applicant
             arrived via DTM, using the same predicate as the server:
               dtmScore IS NOT NULL AND dtmScore <> ''
             Deliberately NOT typeAbiturient == 'DTM' — a DTM applicant stays
             typeAbiturient = ABITURIENT, because type_abit drives contract price and kurs. -->
        <template v-if="isDtm">
          <div class="section-gap">
            <div class="gradient-divider" />
          </div>

          <v-row>
            <v-col cols="12" md="6">
              <div class="info-section">
                <admin-app-show-detail
                  :title="$t('DTM result')"
                  :text="application.dtmScore || '—'"
                />
                <admin-app-show-detail
                  :title="$t('Applicant dtm ID number')"
                  class="pt-4"
                  :text="application.dtmAbiturientNumber || '—'"
                />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-section">
                <admin-app-show-detail :title="$t('DTM Test Paper Result')" />
                <admin-app-show-photo
                  class="pt-2"
                  v-if="application.dtmAbiturientPhoto"
                  :url="$file(application.dtmAbiturientPhoto)"
                />
                <div v-else class="pt-2">—</div>
              </div>
            </v-col>
          </v-row>
        </template>

        <!-- ═══ EXAM RESULT CARD ═══ -->
        <div v-if="examResult || examResultLoading" class="section-gap">
          <div class="gradient-divider" />
        </div>

        <!-- Attempt selector — shown only when a student sat the exam more than once
             (a failed retake keeps its attempt). Click a pill to inspect that attempt. -->
        <div v-if="hasMultipleAttempts && !examResultLoading" class="exam-attempts">
          <span class="exam-attempts__label">{{ $t("Urinishlar") }}:</span>
          <button
            v-for="(a, i) in examAttempts"
            :key="a.sessionId"
            type="button"
            class="exam-attempts__pill"
            :class="{
              'exam-attempts__pill--active': i === selectedAttemptIndex,
              'exam-attempts__pill--passed': a.passed,
              'exam-attempts__pill--failed': a.passed === false,
            }"
            @click="selectAttempt(i)"
          >
            <span class="exam-attempts__dot"></span>
            {{ $t("Urinish") }} {{ a.attemptNumber || (i + 1) }}
            <span class="exam-attempts__score">{{ a.totalScore || 0 }}/{{ a.maxScore || 0 }}</span>
          </button>
        </div>

        <div v-if="examResultLoading" class="exam-card exam-card--loading">
          <div class="exam-card__skeleton"></div>
        </div>

        <div v-else-if="examResult" class="exam-card" :class="{ 'exam-card--passed': examResult.passed, 'exam-card--failed': examResult.passed === false }">
          <div class="exam-card__header">
            <div class="exam-card__title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 10 3 12 0v-5"/></svg>
              {{ $t("Imtihon natijasi") }}
            </div>
            <span class="exam-card__badge" :class="examResult.passed ? 'exam-card__badge--passed' : 'exam-card__badge--failed'">
              {{ examResult.passed ? $t("O'tdi") : $t("O'tmadi") }}
            </span>
          </div>

          <div class="exam-card__body">
            <!-- Score ring -->
            <div class="exam-card__score-block">
              <div class="exam-card__ring">
                <svg viewBox="0 0 100 100" class="exam-card__ring-svg">
                  <circle cx="50" cy="50" r="42" fill="none" stroke-width="8" class="exam-card__ring-bg"/>
                  <circle cx="50" cy="50" r="42" fill="none" stroke-width="8" class="exam-card__ring-fill"
                    :stroke-dasharray="2 * Math.PI * 42"
                    :stroke-dashoffset="2 * Math.PI * 42 * (1 - ((examResult.totalScore || 0) / (examResult.maxScore || 1)))"
                    stroke-linecap="round"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <div class="exam-card__ring-text">
                  <span class="exam-card__ring-score">{{ examResult.totalScore || 0 }}</span>
                  <span class="exam-card__ring-max">/ {{ examResult.maxScore || 0 }}</span>
                </div>
              </div>
              <div class="exam-card__score-label">{{ $t("Ball") }}</div>
            </div>

            <!-- Stats grid -->
            <div class="exam-card__stats">
              <div class="exam-card__stat">
                <div class="exam-card__stat-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div>
                  <div class="exam-card__stat-value">{{ examResult.correctCount || 0 }} / {{ examResult.totalQuestions || 0 }}</div>
                  <div class="exam-card__stat-label">{{ $t("To'g'ri javoblar") }}</div>
                </div>
              </div>
              <div class="exam-card__stat">
                <div class="exam-card__stat-icon exam-card__stat-icon--clock">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                <div>
                  <div class="exam-card__stat-value">{{ formatDuration(examResult.durationSeconds) }}</div>
                  <div class="exam-card__stat-label">{{ $t("Sarflangan vaqt") }}</div>
                </div>
              </div>
              <div class="exam-card__stat">
                <div class="exam-card__stat-icon exam-card__stat-icon--cal">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                </div>
                <div>
                  <div class="exam-card__stat-value">{{ formatExamDate(examResult.startedAt) }}</div>
                  <div class="exam-card__stat-label">{{ $t("Boshlangan vaqt") }}</div>
                </div>
              </div>
              <div class="exam-card__stat" v-if="examResult.finishedAt">
                <div class="exam-card__stat-icon exam-card__stat-icon--flag">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                </div>
                <div>
                  <div class="exam-card__stat-value">{{ formatExamDate(examResult.finishedAt) }}</div>
                  <div class="exam-card__stat-label">{{ $t("Tugallangan vaqt") }}</div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="examResultBySubject.length" class="exam-card__subjects">
            <div class="exam-card__subjects-title">{{ $t("Fanlar bo'yicha natija") }}</div>
            <div class="exam-card__subjects-grid">
              <div
                v-for="row in examResultBySubject"
                :key="row.name"
                class="exam-card__subject"
                :class="{ 'exam-card__subject--full': row.correct === row.total, 'exam-card__subject--zero': row.correct === 0 }"
              >
                <div class="exam-card__subject-name" :title="row.name">{{ row.name }}</div>
                <div class="exam-card__subject-score">
                  <span class="exam-card__subject-correct">{{ row.correct }}</span>
                  <span class="exam-card__subject-sep">/</span>
                  <span class="exam-card__subject-total">{{ row.total }}</span>
                </div>
                <div class="exam-card__subject-bar" aria-hidden="true">
                  <div class="exam-card__subject-bar-fill" :style="{ width: ((row.correct / Math.max(row.total, 1)) * 100) + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Bitrix deal (live crm.deal.get) — full raw fields -->
        <div class="bitrix-card" :class="{ 'bitrix-card--collapsed': bitrixCollapsed }">
          <button
            type="button"
            class="bitrix-card__title"
            @click="bitrixCollapsed = !bitrixCollapsed"
            :aria-expanded="String(!bitrixCollapsed)"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
            {{ $t("Bitrix holati") }}
            <svg class="bitrix-card__chevron" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
          </button>

          <div class="bitrix-card__body" v-show="!bitrixCollapsed">
            <div v-if="bitrixLoading" class="bitrix-card__skeleton">
              <div class="bitrix-card__skeleton-row" v-for="n in 7" :key="n"></div>
            </div>
            <div v-else-if="bitrixError" class="bitrix-card__empty">{{ $t("Bitrix ma'lumotini olishda xatolik") }}</div>
            <div v-else-if="!bitrixDeal || !bitrixDeal.found" class="bitrix-card__empty">{{ $t("Bu abituriyent uchun Bitrix deal topilmadi") }}</div>
            <div v-else>
              <div class="bitrix-card__meta">Deal ID: {{ bitrixDeal.dealId }}</div>
              <table class="bitrix-card__table">
                <tbody>
                  <tr v-for="(val, key) in bitrixDeal.fields" :key="key">
                    <td class="bitrix-card__key">{{ key }}</td>
                    <td class="bitrix-card__val">{{ formatBitrixValue(val) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Gradient divider -->
        <div class="section-gap">
          <div class="gradient-divider" />
        </div>

        <!-- perevod 2mutaxasis -->
        <v-row v-if="application.typeAbiturient != 'ABITURIENT'">
          <v-col cols="12" md="6">
            <template v-if="application.typeAbiturient == 'PEREVOD'">
              <admin-app-show-detail
                v-model="application.reviews.transcriptPhoto"
                :title="$t('Transkript')"
              />
              <admin-app-show-photo
                class="pt-2"
                v-if="application.transcriptPhoto"
                :url="$file(application.transcriptPhoto)"
              />
              <div v-else>—</div>
            </template>
            <template v-else-if="application.typeAbiturient == 'MASTER' || application.typeAbiturient == 'SECOND_SPES'">
              <admin-app-show-detail
                v-model="application.reviews.highSchoolDiplomaIlovaPhoto"
                :title="$t('Diplom')"
              />
              <admin-app-show-photo
                class="pt-2"
                v-if="application.highSchoolDiplomaIlovaPhoto"
                :url="$file(application.highSchoolDiplomaIlovaPhoto)"
              />
              <div v-else>—</div>
            </template>
          </v-col>
          <v-col cols="12" md="6">
            <div class="info-section">
              <admin-app-show-detail
                :title="$t('Hujjat type')"
                :text="application.typeAbiturient || '—'"
                v-model="application.reviews.typeAbiturient"
              />
              <div v-if="application.typeAbiturient == 'PEREVOD'">
                <admin-app-show-detail
                  :title="$t('OTM nomi')"
                  class="pt-4"
                  :text="application.nameOTM"
                  v-model="application.reviews.nameOTM"
                />
                <admin-app-show-detail
                  :title="$t('O`qigan ta`lim yo`nalishi')"
                  class="pt-4"
                  :text="application.nameYonalish"
                  v-model="application.reviews.nameYonalish"
                />
                <admin-app-show-detail
                  :title="$t('finish kurs')"
                  class="pt-4"
                  :text="application.finishKurs"
                  v-model="application.reviews.finishKurs"
                />
              </div>
              <div v-if="application.typeAbiturient == 'SECOND_SPES'">
                <admin-app-show-detail
                  :title="$t('OTM nomi')"
                  class="pt-4"
                  :text="application.nameOTM"
                  v-model="application.reviews.nameOTM"
                />
                <admin-app-show-detail
                  :title="$t('O`qigan ta`lim yo`nalishi')"
                  class="pt-4"
                  :text="application.nameYonalish"
                  v-model="application.reviews.nameYonalish"
                />
              </div>
              <div v-if="application.typeAbiturient == 'MASTER'">
                <admin-app-show-detail
                  :title="$t('OTM nomi')"
                  class="pt-4"
                  :text="application.nameOTM"
                  v-model="application.reviews.nameOTM"
                />
                <admin-app-show-detail
                  :title="$t('O`qigan ta`lim yo`nalishi')"
                  class="pt-4"
                  :text="application.nameYonalish"
                  v-model="application.reviews.nameYonalish"
                />
              </div>
            </div>
          </v-col>
        </v-row>

        <div class="section-gap" v-if="application.typeAbiturient != 'ABITURIENT'">
          <div class="gradient-divider" />
        </div>
      </template>

      <!-- Bottom action buttons -->
      <v-row>
        <v-col cols="12">
          <div class="action-buttons">
            <v-spacer />

            <!-- Save / Cancel (editing mode) -->
            <template v-if="isEditing">
              <button class="act-btn act-btn--reject" type="button" @click="cancelEdit">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                {{ $t("Cancel") }}
              </button>
              <button class="act-btn act-btn--accept" type="button" :disabled="isSaving || hasFormErrors" :title="hasFormErrors ? $t('Please complete all fields correctly') : ''" @click="saveEdit">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                {{ isSaving ? '...' : $t("Save") }}
              </button>
            </template>

            <!-- Accept / Reject / etc (view mode) -->
            <template v-else>
              <button
                v-if="canQabul"
                @click="rejectDialog = true"
                class="act-btn act-btn--reject"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                {{ $t("Reject") }}
              </button>
              <button
                v-if="canQabul"
                :disabled="
                  application.status == 'ACCEPTED' ||
                  application.status == 'PASSED' ||
                  application.status == 'EXAMINED'
                "
                @click="acceptDialog = true"
                class="act-btn act-btn--accept"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                {{ $t("Accept") }}
              </button>

              <button
                v-if="
                  (isSuperAdmin || isQabulQiluvchi || canUpdate) &&
                  (application.status == 'EXAMINED' ||
                    (application.status == 'ACCEPTED' &&
                      application.dtmScore != null))
                "
                @click="passedDialog = true"
                class="act-btn act-btn--accept"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 10 3 12 0v-5"/></svg>
                {{ $t("Pass") }}
              </button>
              <button
                v-if="
                  (isSuperAdmin || isQabulQiluvchi || isReadonly) &&
                  application.status == 'ACCEPTED' &&
                  !application.paid
                "
                @click="paidDialog = true"
                class="act-btn act-btn--accept"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                {{ $t("Tavsiya qilish") }}
              </button>
            </template>
          </div>
        </v-col>
      </v-row>
    </v-form>

    <app-confirm
      v-model="acceptDialog"
      :title="$t('Accept')"
      :text="$t('Accept?')"
      :confirmText="$t('Yes, accept')"
      @accept="onAccept"
    />

    <app-confirm
      v-model="passedDialog"
      :title="$t('Pass')"
      :text="$t('Passed?')"
      :confirmText="$t('Yes, pass')"
      @accept="onPassed"
    />

    <app-confirm
      v-model="rejectDialog"
      :title="$t('Reject')"
      :text="$t('Reject?')"
      :confirmText="$t('Yes, reject')"
      @accept="onRejects"
    />

    <app-confirm
      v-model="paidDialog"
      :title="$t('Tavsiya qilish')"
      :text="$t('paid?')"
      :confirmText="$t('Yes, confirm')"
      :confirmVariant="'success'"
      @accept="onSuggested"
    />
  </div>
</template>

<style lang="scss" scoped>
/* ═══════════════════════════════════════════════════════════
   LIGHT THEME (default)
   ═══════════════════════════════════════════════════════════ */

/* ── Info Sections (read mode) ── */
.info-section {
  height: 100%;
  border-left: 3px solid #e6e8ec;
  padding-left: 16px;
}

.detail-title {
  font-size: 13px;
  color: #667085;
  font-weight: 500;
}

.detail-text {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin-top: 2px;
}

.section-gap { padding: 24px 0; }

.gradient-divider {
  height: 1px;
  background: #e6e8ec;
}

/* ── Exam attempt selector ── */
.exam-attempts {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.exam-attempts__label {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin-right: 2px;
}

.exam-attempts__pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}

.exam-attempts__pill:hover {
  border-color: #cbd5e1;
}

.exam-attempts__pill--active {
  border-color: #6366f1;
  background: #eef2ff;
  box-shadow: 0 0 0 1px #6366f1;
}

.exam-attempts__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #cbd5e1;
  flex: 0 0 auto;
}

.exam-attempts__pill--passed .exam-attempts__dot { background: #16a34a; }
.exam-attempts__pill--failed .exam-attempts__dot { background: #dc2626; }

.exam-attempts__score {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
}

/* ── Exam Result Card ── */
.exam-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
}

.exam-card--loading {
  padding: 48px;
  display: flex;
  justify-content: center;
}

.exam-card__skeleton {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #94a3b8;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.exam-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
}

.exam-card__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
}

.exam-card__badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.exam-card__badge--passed {
  background: #dcfce7;
  color: #15803d;
}

.exam-card__badge--failed {
  background: #fef2f2;
  color: #dc2626;
}

.exam-card__body {
  display: flex;
  align-items: center;
  gap: 40px;
  padding: 24px 20px;
  flex-wrap: wrap;
}

.exam-card__score-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.exam-card__ring {
  position: relative;
  width: 110px;
  height: 110px;
}

.exam-card__ring-svg {
  width: 100%;
  height: 100%;
}

.exam-card__ring-bg {
  stroke: #e2e8f0;
}

.exam-card--passed .exam-card__ring-fill {
  stroke: #22c55e;
  transition: stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.exam-card--failed .exam-card__ring-fill {
  stroke: #ef4444;
  transition: stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.exam-card__ring-text {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.exam-card__ring-score {
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.exam-card__ring-max {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
}

.exam-card__score-label {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.exam-card__stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  flex: 1;
  min-width: 0;
}

@media (max-width: 600px) {
  .exam-card__stats {
    grid-template-columns: 1fr;
  }
  .exam-card__body {
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
  }
}

.exam-card__stat {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.exam-card__stat-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #dcfce7;
  color: #15803d;
}

.exam-card__stat-icon--clock {
  background: #ede9fe;
  color: #7c3aed;
}

.exam-card__stat-icon--cal {
  background: #dbeafe;
  color: #2563eb;
}

.exam-card__stat-icon--flag {
  background: #fef3c7;
  color: #d97706;
}

.exam-card__stat-value {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.exam-card__stat-label {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 1px;
}

.exam-card__subjects {
  margin: 4px 20px 0;
  padding: 18px 0 22px;
  border-top: 1px dashed #e5e7eb;
}

.theme--dark .exam-card__subjects {
  border-top-color: #2a2c3a;
}

.exam-card__subjects-title {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
}

.theme--dark .exam-card__subjects-title {
  color: #cbd5e1;
}

.exam-card__subjects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
}

.exam-card__subject {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  align-items: center;
  gap: 4px 10px;
  transition: background 0.15s, border-color 0.15s;
}

.theme--dark .exam-card__subject {
  background: #1f2030;
  border-color: #2a2c3a;
}

.exam-card__subject--full {
  background: #ecfdf5;
  border-color: #a7f3d0;
}
.theme--dark .exam-card__subject--full {
  background: rgba(16, 185, 129, 0.12);
  border-color: rgba(16, 185, 129, 0.35);
}

.exam-card__subject--zero {
  background: #fef2f2;
  border-color: #fecaca;
}
.theme--dark .exam-card__subject--zero {
  background: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.35);
}

.exam-card__subject-name {
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.theme--dark .exam-card__subject-name {
  color: #e2e8f0;
}

.exam-card__subject-score {
  font-size: 13px;
  font-variant-numeric: tabular-nums lining-nums;
  font-weight: 600;
  color: #0f172a;
}
.theme--dark .exam-card__subject-score {
  color: #f1f5f9;
}
.exam-card__subject-correct { color: #16a34a; }
.exam-card__subject--zero .exam-card__subject-correct { color: #ef4444; }
.exam-card__subject-sep { color: #cbd5e1; margin: 0 2px; }
.exam-card__subject-total { color: #475569; }
.theme--dark .exam-card__subject-total { color: #94a3b8; }

.exam-card__subject-bar {
  grid-column: 1 / -1;
  height: 4px;
  background: rgba(148, 163, 184, 0.25);
  border-radius: 999px;
  overflow: hidden;
}
.exam-card__subject-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #34d399, #10b981);
  border-radius: 999px;
  transition: width 0.4s cubic-bezier(.2,.8,.2,1);
}
.exam-card__subject--zero .exam-card__subject-bar-fill {
  background: linear-gradient(90deg, #f87171, #ef4444);
}

/* ── Action Buttons (Accept / Reject / etc) ── */
.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 24px;
}

.act-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.act-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.act-btn--accept {
  background: #ffd451;
  color: #1a1500;
  border-color: #f5c542;
}
.act-btn--accept:hover:not(:disabled) { background: #f5c542; }

.act-btn--reject {
  background: #fff;
  color: #dc2626;
  border-color: #d0d5dd;
}
.act-btn--reject:hover:not(:disabled) { background: #fef2f2; }

/* ── Edit Fields ── */
.ef-group {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ef { max-width: 420px; }

.ef__label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #667085;
  margin-bottom: 5px;
}

.ef__input {
  display: block;
  width: 100%;
  padding: 9px 12px;
  font-size: 14px;
  font-family: inherit;
  color: #111318;
  background: #fff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

/* Hand-made DatePicker (Tug'ilgan sanasi) — match its box to the .ef__input fields around it. */
.ef__datepicker::v-deep .dp__input {
  border: 1px solid #d0d5dd;
  border-radius: 8px;
}

/* ── DTM Edit Block ──
   Boxed so the DTM values read as one group rather than as loose exam fields. They decide
   whether the entrance exam is waived, so mistaking them for the entrance-exam score is the
   specific confusion worth designing against. */
.ef-dtm {
  margin-top: 20px;
  padding: 16px;
  border: 1px solid #e6e8ec;
  border-radius: 10px;
  background: #fbfcfd;
}

.ef-dtm__title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 12px;
}

.ef-dtm__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

@media (max-width: 600px) {
  .ef-dtm__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* The block spans the full form width, but the uploader should not: left unbounded it
   stretches to the whole page and dwarfs every other document control on the form. */
.ef-dtm__file {
  margin-top: 16px;
  max-width: 420px;
}

/* ── Photo Edit Section ── */
.ef-photos-edit {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
}

/* ── Custom Dropdown ── */
.ef-dropdown {
  position: relative;
}

.ef-dropdown__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 9px 12px;
  font-size: 14px;
  font-family: inherit;
  color: #111318;
  background: #fff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.ef-dropdown__trigger svg {
  transition: transform 0.2s ease;
  color: #667085;
  flex-shrink: 0;
}

.ef-dropdown--open .ef-dropdown__trigger {
  border-color: #ffd451;
  box-shadow: 0 0 0 3px rgba(255, 212, 81, 0.2);
}

.ef-dropdown--open .ef-dropdown__trigger svg {
  transform: rotate(180deg);
}

.ef-dropdown__menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 10;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.ef-dropdown__item {
  padding: 9px 12px;
  font-size: 14px;
  color: #344054;
  cursor: pointer;
  transition: background 0.1s ease;
}

.ef-dropdown__item:hover {
  background: #f9fafb;
}

.ef-dropdown__item--active {
  background: #ffd451;
  color: #111318;
  font-weight: 500;
}

.ef-dropdown__item--active:hover {
  background: #f5c542;
}

.ef-dropdown__placeholder {
  color: #98a2b3;
}

.ef-dropdown__menu--tall {
  max-height: 320px;
  display: flex;
  flex-direction: column;
}

.ef-dropdown__search {
  padding: 6px;
  border-bottom: 1px solid #e5e7eb;
}

.ef-dropdown__search-input {
  display: block;
  width: 100%;
  padding: 7px 10px;
  font-size: 13px;
  font-family: inherit;
  color: #111318;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  outline: none;
}

.ef-dropdown__search-input:focus {
  border-color: #00274c;
}

.ef-dropdown__scroll {
  overflow-y: auto;
  max-height: 260px;
}

.ef-dropdown__empty {
  padding: 12px;
  text-align: center;
  color: #98a2b3;
  font-size: 13px;
}

.ef-dropdown__trigger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ef__input:focus {
  border-color: #00274c;
  box-shadow: 0 0 0 3px rgba(0, 39, 76, 0.2);
}

.ef__input--error {
  border-color: #dc2626 !important;
  background: #fef2f2 !important;
}
.ef__input--error:focus {
  border-color: #dc2626 !important;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.18) !important;
}

.ef__error-text {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.3;
  color: #dc2626;
}

.ef__readonly {
  padding: 9px 12px;
  font-size: 14px;
  color: #98a2b3;
  background: #f9fafb;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
}

.ef-photos {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ═══════════════════════════════════════════════════════════
   DARK THEME — using .app-show-content--dark wrapper
   (avoids scoped + .theme--dark parent selector issues)
   ═══════════════════════════════════════════════════════════ */
.app-show-content--dark {
  /* Info sections */
  .info-section { border-left-color: #33354a; }
  .detail-title { color: #8b8d9e; }
  .detail-text { color: #e4e5eb; }
  .gradient-divider { background: #2a2c3a; }

  /* Exam attempt selector */
  .exam-attempts__label { color: #8b8d9e; }
  .exam-attempts__pill {
    color: #e4e5eb;
    background: #1c1e2a;
    border-color: #33354a;
  }
  .exam-attempts__pill:hover { border-color: #444764; }
  .exam-attempts__pill--active {
    border-color: #7c83ff;
    background: #262a44;
    box-shadow: 0 0 0 1px #7c83ff;
  }
  .exam-attempts__dot { background: #444764; }
  .exam-attempts__pill--passed .exam-attempts__dot { background: #22c55e; }
  .exam-attempts__pill--failed .exam-attempts__dot { background: #f87171; }
  .exam-attempts__score { color: #6b6d80; }

  /* Action buttons */
  .act-btn--accept {
    background: #ffd451;
    color: #111318;
    border-color: #f5c542;
  }
  .act-btn--accept:hover:not(:disabled) { background: #ffdc6e; }

  .act-btn--reject {
    background: #2a2c3a;
    color: #f87171;
    border-color: #33354a;
  }
  .act-btn--reject:hover:not(:disabled) { background: #33354a; }

  /* Edit fields */
  .ef__label { color: #8b8d9e; }

  .ef__input {
    background: #282a36;
    color: #e4e5eb;
    border-color: #3a3c4e;
  }

  .ef__input:focus {
    border-color: #ffd451;
    box-shadow: 0 0 0 3px rgba(255, 212, 81, 0.15);
  }

  .ef-dropdown__trigger {
    background: #282a36;
    color: #e4e5eb;
    border-color: #3a3c4e;
  }

  .ef-dropdown__trigger svg { color: #8b8d9e; }

  .ef-dropdown--open .ef-dropdown__trigger {
    border-color: #ffd451;
    box-shadow: 0 0 0 3px rgba(255, 212, 81, 0.15);
  }

  .ef-dropdown__menu {
    background: #282a36;
    border-color: #3a3c4e;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  }

  .ef-dropdown__item {
    color: #d5d6de;
  }

  .ef-dropdown__item:hover {
    background: #33354a;
  }

  .ef-dropdown__item--active {
    background: #ffd451;
    color: #111318;
  }

  .ef-dropdown__item--active:hover {
    background: #f5c542;
  }

  .ef__readonly {
    background: #1e1f2a;
    color: #606275;
    border-color: #2a2c3a;
  }

  .ef-dropdown__placeholder {
    color: #606275;
  }

  .ef-dropdown__search {
    border-bottom-color: #3a3c4e;
  }

  .ef-dropdown__search-input {
    background: #1e1f2a;
    color: #e4e5eb;
    border-color: #3a3c4e;
  }

  .ef-dropdown__search-input:focus {
    border-color: #ffd451;
  }

  .ef-dropdown__empty {
    color: #606275;
  }

  .ef-dropdown__trigger:disabled {
    opacity: 0.4;
  }

  /* Exam card dark */
  .exam-card {
    background: #1e1f2a;
    border-color: #33354a;
  }
  .exam-card__header { border-bottom-color: #33354a; }
  .exam-card__title { color: #e4e5eb; }
  .exam-card__ring-bg { stroke: #33354a; }
  .exam-card__ring-score { color: #e4e5eb; }
  .exam-card__ring-max { color: #606275; }
  .exam-card__score-label { color: #606275; }
  .exam-card__stat-value { color: #e4e5eb; }
  .exam-card__stat-label { color: #606275; }
  .exam-card__skeleton { border-color: #33354a; border-top-color: #606275; }
}

.bitrix-card {
  margin-top: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background: #fff;

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    // button reset
    appearance: none;
    background: none;
    border: none;
    padding: 0;
    margin: 0 0 12px;
    font-family: inherit;
    font-weight: 600;
    font-size: 15px;
    color: #1e293b;
    text-align: left;
    cursor: pointer;
    user-select: none;
    & > svg:first-of-type { color: #2f6fed; }
  }

  &__chevron {
    margin-left: auto;
    color: #94a3b8;
    flex-shrink: 0;
    transition: transform .22s ease;
    transform: rotate(180deg); // expanded → points up
  }

  &--collapsed &__title { margin-bottom: 0; }
  &--collapsed &__chevron { transform: rotate(0deg); }

  &__meta { font-size: 12px; color: #64748b; margin-bottom: 8px; }
  &__empty { font-size: 14px; color: #64748b; padding: 12px 0; }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    td { border-top: 1px solid #f1f5f9; padding: 6px 8px; vertical-align: top; }
  }

  &__key {
    width: 42%;
    color: #475569;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    word-break: break-all;
  }

  &__val { color: #0f172a; white-space: pre-wrap; word-break: break-word; }

  &__skeleton-row {
    height: 16px;
    border-radius: 6px;
    margin: 8px 0;
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 37%, #f1f5f9 63%);
    background-size: 400% 100%;
    animation: bitrixShimmer 1.4s ease infinite;
  }
}

@keyframes bitrixShimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

/* Excel green, matching the import button on /applications so the two read as one feature. */
.excel-pill {
  display: inline-flex;
  align-items: center;
  background: #217346;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .3px;
  padding: 5px 12px;
  border-radius: 999px;
  margin-bottom: 12px;
}
</style>
